import { useState } from 'react';
import { Notice, Panel } from '../components/Bits';
import { api } from '../lib/api';

/** Startseite: Lobby eröffnen oder mit einem Code beitreten. */
export function Landing() {
  const [password, setPassword] = useState('');
  const [lobbyPassword, setLobbyPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{
    code: string;
    host: string;
    overlay: string;
    join: string;
    lobbyPassword: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  async function open() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.createLobby(password, lobbyPassword);
      const base = window.location.origin;
      setCreated({
        code: res.code,
        host: `${base}/game/${res.code}/host#${res.hostToken}`,
        join: `${base}/game/${res.code}`,
        overlay: res.links.overlay.replace(/^https?:\/\/[^/]+/, base),
        lobbyPassword,
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page stack">
      <header>
        <h1>Schatten über Noxbury</h1>
        <p className="muted">
          Ein Dorf, ein paar Vampire und eine Stadtversammlung, die jeden Abend jemanden verliert.
        </p>
      </header>

      {created ? (
        <Panel title={`Lobby ${created.code} steht`}>
          <p>Bewahre den ersten Link gut auf: Er ist dein Zugang zum Regiepult und wird nur einmal gezeigt.</p>
          <div className="stack">
            <LinkRow label="Regiepult (nur für dich)" href={created.host} />
            <LinkRow label="Für die Spieler" href={created.join} />
            {created.lobbyPassword && (
              <div className="field">
                <label>Lobby-Passwort für die Spieler</label>
                <input readOnly value={created.lobbyPassword} onFocus={(e) => e.currentTarget.select()} />
                <p className="tiny muted" style={{ marginTop: '0.3rem' }}>
                  Ohne dieses Passwort kommt niemand in die Lobby – zusammen mit dem Link weitergeben.
                </p>
              </div>
            )}
            <LinkRow label="Browserquelle für OBS" href={created.overlay} />
          </div>
          <a className="button primary" href={created.host} style={{ marginTop: '1rem', display: 'inline-block' }}>
            Regiepult öffnen
          </a>
        </Panel>
      ) : (
        <div className="stack">
          <Panel title="Als Spielleiter eröffnen">
            <div className="field">
              <label htmlFor="pw">Spielleiter-Passwort</label>
              <input
                id="pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void open()}
              />
            </div>
            <div className="field">
              <label htmlFor="lobbypw">Lobby-Passwort für die Spieler</label>
              <input
                id="lobbypw"
                value={lobbyPassword}
                maxLength={128}
                placeholder="z. B. blutmond"
                onChange={(e) => setLobbyPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void open()}
              />
              <p className="tiny muted" style={{ marginTop: '0.3rem' }}>
                Nur wer dieses Passwort kennt, kann beitreten. Leer lassen heißt: offen für jeden mit dem Link.
              </p>
            </div>
            <button type="button" className="primary" disabled={busy} onClick={() => void open()}>
              Lobby eröffnen
            </button>
            {error && <Notice>{error}</Notice>}
          </Panel>

          <Panel title="Einer Runde beitreten">
            <div className="field">
              <label htmlFor="code">Lobby-Code</label>
              <input
                id="code"
                value={code}
                maxLength={6}
                placeholder="z. B. KQ7RTM"
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && code.length === 6 && (window.location.href = `/game/${code}`)}
              />
            </div>
            <button type="button" disabled={code.length !== 6} onClick={() => (window.location.href = `/game/${code}`)}>
              Weiter
            </button>
          </Panel>
        </div>
      )}
    </main>
  );
}

function LinkRow({ label, href }: { label: string; href: string }) {
  return (
    <div className="field">
      <label>{label}</label>
      <input readOnly value={href} onFocus={(e) => e.currentTarget.select()} />
      <button type="button" className="tiny" style={{ marginTop: '0.3rem' }} onClick={() => void navigator.clipboard?.writeText(href)}>
        Kopieren
      </button>
    </div>
  );
}
